/**
  Connectors API wrapper

  Handles API calls for connector management (CRM integrations)
**/

import { api } from "../common/utils"
import {
  CONNECTORS_ENDPOINT,
  CONNECTOR_ENDPOINT,
  CONNECTOR_LOGS_ENDPOINT,
  CONNECTOR_TEST_ENDPOINT,
  CONNECTOR_PREVIEW_ENDPOINT
} from "../common/constants"
import {
  ConnectorDetails,
  FindConnectorsResponse,
  FindConnectorLogsResponse
} from "../types"

const connectors = {
  find: async (params: {
    brand_id?: string
    owner?: string
    type?: string
    active?: number
    direction?: string
    data_type?: string
    limit?: number
    offset?: number
  }): Promise<FindConnectorsResponse> => {
    const queryParams = new URLSearchParams()
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        queryParams.append(key, String(value))
      }
    })
    const { data } = await api.get(
      `${CONNECTORS_ENDPOINT}?${queryParams.toString()}`
    )
    return data
  },

  findDetails: async (id: string): Promise<ConnectorDetails> => {
    const { data } = await api.get(`${CONNECTOR_ENDPOINT}?id=${id}`)

    // Extract connector from response - API returns { status, connector: [...] }
    let result
    if (data.connector && Array.isArray(data.connector)) {
      result = data.connector[0]
    } else if (data.connectors && Array.isArray(data.connectors)) {
      result = data.connectors[0]
    } else {
      result = data
    }

    // Ensure the id is present in the result
    if (result && !result.id) {
      result.id = id
    }

    return result
  },

  updateOrCreate: async (
    payload: Partial<ConnectorDetails>
  ): Promise<{
    status: string
    message?: string
    connector_id?: string
    endpoint_url?: string
  }> => {
    const { data } = await api.post(CONNECTOR_ENDPOINT, payload)
    return data
  },

  delete: async (id: string): Promise<{ status: string; message?: string }> => {
    const { data } = await api.delete(`${CONNECTOR_ENDPOINT}?id=${id}`)
    return data
  },

  findLogs: async (params: {
    connector_id?: string
    record_id?: string
    status?: string
    limit?: number
    offset?: number
  }): Promise<FindConnectorLogsResponse> => {
    const queryParams = new URLSearchParams()
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        queryParams.append(key, String(value))
      }
    })
    const { data } = await api.get(
      `${CONNECTOR_LOGS_ENDPOINT}?${queryParams.toString()}`
    )
    return data
  },

  testConnection: async (params: {
    connector_id: string
    record_id?: string
    test_data?: any
  }): Promise<{ status: string; message?: string; test_result?: any }> => {
    const { data } = await api.post(CONNECTOR_TEST_ENDPOINT, params)
    return data
  },

  preview: async (params: {
    connector_id: string
    lead_id?: string
    subscription_id?: string
    test_data?: any
  }): Promise<{
    status: string
    payload?: any
    webhook_url?: string
    headers?: Record<string, string>
    message?: string
  }> => {
    const { data } = await api.post(CONNECTOR_PREVIEW_ENDPOINT, params)
    return data
  }
}

export default connectors
